import { makeAutoObservable, runInAction, toJS } from 'mobx';
import { RootStore } from 'store/rootStore';
import { normDateToShortAmericanDate } from '@insurancemenu/imenu-components-lib/dist/helpers/dateFormatters';
import { ChecklistOption, EnrollmentPeriod } from 'types/enrollmentService';
import {
  Subscriber,
  ProductInfo,
  EeClassWithValidation,
  ProductLineSubRate,
  SubChecklistCard,
  RequiredSubInfo,
  RequiredSubInfoPresenter,
  PersonInfo,
} from 'types/electionService';
import EnrollmentPeriodModule, { INITIAL_ENROLLMENT_OBJECT } from 'store/modules/enrollment/enrollmentPeriodModule';
import { INITIAL_FORM_OBJECT, requestFormById } from 'api/operations/formService';
import {
  completeSubscriberChecklistStep,
  getEmployeeClasses,
  getProducts,
  getRequiredSubInfo,
  getStatistics,
  getSubscriberWithChoices,
} from 'api/operations/electionService';
import { createRequiredSubInfoPresenter } from 'global/utils/election/electionPresenters';
import { getAdditionalFormIdsFromSubscriberChoices } from 'global/utils/election/productLineValidation';
import { getTaskByEntityId, sortDependentsInSubscriber } from 'global/services-helpers/electionService';
import { Group } from 'types/groupService';
import { getGroupById } from 'api/operations/groupService';
import { getSubscriberTaskChecklistURL } from 'global/checklists-helpers/SubscriberChecklistHelper';
import { bpHistory } from 'global/bpHistory';

// TODO refactor store to use new approaches before covering with tests
export default class ReviewSubscriberStore {
  rootStore: RootStore;
  private _group: Pick<Group, 'name' | 'id' | 'contactInfo'> | null = null;
  private _enrollmentPeriodModule = new EnrollmentPeriodModule();

  public loading = false;
  public subscriber: Subscriber | null = null;
  public products: ProductInfo[] = [];
  public employeeClasses: EeClassWithValidation[] = [];
  public enrollmentPeriod: EnrollmentPeriod = INITIAL_ENROLLMENT_OBJECT;
  public subscriberRates: ProductLineSubRate[] = [];
  public subscriberFormData: Record<string, string> = {};
  public requiredSubInfo: RequiredSubInfo | null = null;

  public get group(): Pick<Group, 'name' | 'id' | 'contactInfo'> | null {
    return this._group;
  }

  private get _periodId(): string {
    return this._enrollmentPeriodModule.enrollmentCoreData?.id || '';
  }

  public get taskChecklistOptions(): ChecklistOption<SubChecklistCard>[] {
    return this.subscriber ? getTaskByEntityId(this.subscriber, this._periodId)?.checklist?.checklistOptions || [] : [];
  }

  /**
   * Enrollment required-subscriber rules for SubscriberCard and validation
   */
  public get requiredSubInfoPresenter(): RequiredSubInfoPresenter | undefined {
    return createRequiredSubInfoPresenter(toJS(this.requiredSubInfo), 'enrollment') ?? undefined;
  }

  private setLoading(loadingStatus: boolean): void {
    this.loading = loadingStatus;
  }

  public async getInitialData(subscriberId: string, periodId: string, groupId: string): Promise<void> {
    this.setLoading(true);
    const [group] = await Promise.all([
      getGroupById(groupId),
      this._enrollmentPeriodModule.uploadEnrollmentPeriod(periodId),
      this._getProducts(periodId),
      this._getRequiredSubInfo(),
      this._getClasses(groupId, periodId),
      this._getStatistic(subscriberId, periodId, groupId),
    ]);

    void runInAction(async (): Promise<void> => {
      this._group = group ? { name: group.name, id: group.id, contactInfo: group.contactInfo } : null;

      await this._getSubscriber(subscriberId);
      this.setLoading(false);
    });
  }

  private async _getSubscriber(subscriberId: string): Promise<void> {
    const subscriberResult = await getSubscriberWithChoices(subscriberId, this._periodId);
    await runInAction(async (): Promise<void> => {
      this.subscriber = sortDependentsInSubscriber(subscriberResult);

      const subscriberFormIds = [
        this.requiredSubInfo?.editSubFormId || '',
        // Retrieve additional form IDs based on the selected choice.
        ...getAdditionalFormIdsFromSubscriberChoices(this.subscriber, this.requiredSubInfoPresenter),
      ].filter(Boolean);

      if (subscriberFormIds.length > 0) {
        await Promise.all(
          subscriberFormIds.map(async (formId) => {
            const formData = await this.getSubscriberFormData(formId);
            if (Object.values(formData).length > 0) {
              // Show additional subscriber info from required and waiver forms at the end.
              runInAction(() => {
                this.subscriberFormData = { ...toJS(this.subscriberFormData), ...formData };
              });
            }
          }),
        );
      }
    });
  }

  private async getSubscriberFormData(formId: string): Promise<Record<string, string>> {
    const res = await requestFormById(formId);
    return runInAction(() => {
      const form = res || INITIAL_FORM_OBJECT;
      const convertedSubscriber = toJS(this.subscriber);
      const answers: PersonInfo = convertedSubscriber?.common || { firstName: '', lastName: '' };

      /**
       * Construct a representation of form questions including their types, labels,
       * and corresponding lists of input option IDs.
       * The IDs within inputOptionIds correspond to keys in the subscriber.common data.
       */
      const questionMetadata: Record<string, { label: string; inputOptionIds: string[] }> = {};

      form.sections.forEach((section) => {
        section.layout.forEach((layout) => {
          if (layout.type === 'CHECKBOX') {
            questionMetadata[layout.key] = {
              label: layout.label,
              inputOptionIds: [layout.key],
            };
          } else if (layout.type !== 'TEXT' && layout.type !== 'CUSTOM') {
            questionMetadata[layout.key] = {
              label: layout.label,
              // Account for scenarios where a single question contains multiple input options.
              // For example, consider a question like the subscriber's name, which may include
              // first, middle, and last names as separate input options.
              inputOptionIds: layout.inputOptions.map(({ id }) => id),
            };
          }
        });
      });

      /**  Transformed the questions into a structured format:
       * - Each question label serves as a key.
       * - Corresponding values consist of answers obtained from all identified input option IDs(from subscriber.common).
       */
      const formData: Record<string, string> = {};

      Object.values(questionMetadata).forEach(({ label, inputOptionIds }) => {
        formData[label] = inputOptionIds
          .map((id) => {
            if (id === 'dob') {
              return answers ? normDateToShortAmericanDate(answers[id]) : '';
            }
            return answers[id];
          })
          .join(' ');
      });

      return formData;
    });
  }

  private async _getStatistic(subscriberId: string, periodId: string, groupId: string): Promise<void> {
    const res = await getStatistics(groupId, periodId);
    runInAction(() => {
      const subscriberRates = res.subRates?.[subscriberId] || [];
      this.subscriberRates = subscriberRates;
    });
  }

  private async _getProducts(periodId: string): Promise<void> {
    const products = await getProducts(periodId);
    runInAction(() => {
      this.products = products;
    });
  }

  private async _getClasses(groupId: string, periodId: string): Promise<void> {
    const classes = await getEmployeeClasses(groupId, periodId);
    runInAction(() => {
      this.employeeClasses = classes;
    });
  }

  private async _getRequiredSubInfo(): Promise<void> {
    const domainId = this.rootStore.domainStoreV2.domainHeader.id;
    const requiredSubInfo = await getRequiredSubInfo(domainId);
    runInAction(() => {
      this.requiredSubInfo = requiredSubInfo;
    });
  }

  public async completeElectionChangeStatus(subId: string): Promise<void> {
    this.setLoading(true);
    await completeSubscriberChecklistStep({
      subId,
      entityId: this._periodId,
      card: SubChecklistCard.REVIEW_AND_CONFIRM,
    });
    runInAction(() => {
      this.setLoading(false);
    });
  }

  public get cartId(): string {
    return this._enrollmentPeriodModule.enrollmentProposalData?.confirmedProposal?.cartId || '';
  }

  public onBackRedirect(): void {
    bpHistory.push(
      getSubscriberTaskChecklistURL({
        entityId: this._periodId,
        subId: this.subscriber?.id || '',
        groupId: this._group?.id || '',
      }),
    );
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, {}, { autoBind: true });
  }
}
